import { useCallback, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { getDonationStatus } from "../queries"

function useDonationStatusPoller({
  id,
  waitBetweenRequests = 1000,
  giveUpAfter = 10000,
}: {
  id: string
  waitBetweenRequests?: number
  giveUpAfter?: number
}) {
  const navigate = useNavigate()
  const didGiveUpRef = useRef<boolean>(false)
  const giveUpTimeoutRef = useRef<number>()
  const statusCheckTimeoutRef = useRef<number>()

  const checkStatus = useCallback(async () => {
    if (didGiveUpRef.current) {
      navigate("donation_processing", {
        state: { nondeterministicError: false },
      })
      return
    }

    const result = await getDonationStatus({ id })

    if (result.paymentStatus === "pending" || result.paymentStatus === null) {
      statusCheckTimeoutRef.current = window.setTimeout(
        checkStatus,
        waitBetweenRequests,
      )
    } else if (result.paymentStatus === "succeeded") {
      navigate("donation_thank_you", {
        state: { recurring: false },
      })
    } else if (result.paymentStatus === "failed") {
      navigate("donation_failure", {
        state: { errorMessage: result.message },
      })
    }
  }, [navigate, waitBetweenRequests, id])

  useEffect(() => {
    giveUpTimeoutRef.current = window.setTimeout(() => {
      didGiveUpRef.current = true
    }, giveUpAfter)

    checkStatus()

    return () => {
      if (giveUpTimeoutRef.current) {
        clearTimeout(giveUpTimeoutRef.current)
      }
      if (statusCheckTimeoutRef.current) {
        clearTimeout(statusCheckTimeoutRef.current)
      }
    }
  }, [giveUpAfter, checkStatus])
}

interface DonationStatusProps {
  id: string
}

export function DonationStatus({ id }: DonationStatusProps) {
  useDonationStatusPoller({ id })

  return null
}
